import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
  AmpelBadgeComponent,
  ampelFor,
  AmpelState,
  countOpenDefects,
} from '../../../components/ampel-badge/ampel-badge.component';
import { ResPillComponent, ResPillStatus } from '../../../components/res-pill/res-pill.component';
import { DefectRefChipsComponent } from '../../../components/defect-ref-chips/defect-ref-chips.component';
import { StackBarComponent } from '../../../components/stack-bar/stack-bar.component';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleasePlanCase, ReleasePlanResult, ReleaseResultsFilters } from '../../../models/release-results.model';
import { TestRunLink, testRunExecutionsLink } from '../../../utils/test-run-routes';
import { ResultStatus, TestRunSource } from '../../../models/test-run.model';
import { passRateTone, releaseResultsFilters } from '../test-results.utils';
import { ReleaseListState } from '../test-results.types';

interface PlansState {
  loading: boolean;
  error: boolean;
  data: ReleasePlanResult[] | null;
}

interface PlansRequest {
  projectId: string;
  releaseId: string | null;
  filters: ReleaseResultsFilters;
}

interface PlanRow {
  plan: ReleasePlanResult;
  ausgefuehrt: number;
  passRate: number | null;
  fehler: number;
  ampel: AmpelState;
}

export interface PlanCaseResult {
  pill: ResPillStatus;
  labelKey: string;
}

const CASE_RESULT: Partial<Record<ResultStatus, PlanCaseResult>> = {
  PASSED: { pill: 'passed', labelKey: 'testResults.plaene.status.passed' },
  FAILED: { pill: 'failed', labelKey: 'testResults.plaene.status.failed' },
  BLOCKED: { pill: 'blocked', labelKey: 'testResults.plaene.status.blocked' },
};
const NOT_RUN: PlanCaseResult = { pill: 'not-run', labelKey: 'testResults.plaene.status.notRun' };

/** Letztes Ergebnis eines Testfalls im Plan; alles außer Bestanden/Fehlgeschlagen/Blockiert zählt als nicht ausgeführt. */
export function planCaseResult(status: ResultStatus | null): PlanCaseResult {
  return (status && CASE_RESULT[status]) || NOT_RUN;
}

/**
 * Sicht „Testpläne" (page-release-freigabe.jsx:452-524): je Plan des Releases Fortschritt,
 * Pass-Rate, Fehler und Ampel; aufgeklappt die Testfälle mit ihrem letzten Ergebnis.
 * Lädt mit denselben Filtern wie die Freigabe-Karte, damit die Summen übereinstimmen.
 */
@Component({
  selector: 'app-plan-ergebnis-tabelle',
  standalone: true,
  imports: [
    TranslatePipe,
    MatIcon,
    RouterLink,
    NgTemplateOutlet,
    AmpelBadgeComponent,
    ResPillComponent,
    StackBarComponent,
    DefectRefChipsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './plan-ergebnis-tabelle.component.html',
  styleUrl: './plan-ergebnis-tabelle.component.scss',
})
export class PlanErgebnisTabelleComponent {
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releaseId = input<string | null>(null);
  readonly releasesState = input<ReleaseListState>('loaded');
  readonly environment = input<string | null>(null);
  readonly source = input<TestRunSource | null>(null);

  /** Plan-Key für die Sicht „Testläufe" mit Plan-Filter. */
  readonly laeufeOeffnen = output<string | null>();

  protected readonly state = signal<PlansState>({ loading: false, error: false, data: null });
  private readonly expanded = signal<ReadonlySet<string>>(new Set());

  protected readonly caseResult = planCaseResult;

  protected readonly rows = computed<PlanRow[]>(() =>
    (this.state().data ?? []).map((plan) => {
      const ausgefuehrt = plan.gesamt - plan.offen;
      return {
        plan,
        ausgefuehrt,
        passRate: ausgefuehrt ? Math.round((plan.passed / ausgefuehrt) * 100) : null,
        fehler: plan.failed + plan.blocked,
        ampel: ampelFor(plan, countOpenDefects(plan.faelle)),
      };
    }),
  );

  private readonly request = computed<PlansRequest>(() => {
    return {
      projectId: this.projectId(),
      releaseId: this.releaseId(),
      filters: releaseResultsFilters(this.environment(), this.source()),
    };
  });

  constructor() {
    toObservable(this.request)
      .pipe(
        switchMap((req) => {
          if (!req.releaseId) return of<PlansState>({ loading: false, error: false, data: null });
          return this.resultsService.getPlans(req.projectId, req.releaseId, req.filters).pipe(
            map((data): PlansState => ({ loading: false, error: false, data })),
            catchError(() => of<PlansState>({ loading: false, error: true, data: null })),
            startWith<PlansState>({ loading: true, error: false, data: null }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.state.set(state));
  }

  protected isOpen(planId: string): boolean {
    return this.expanded().has(planId);
  }

  protected toggle(planId: string): void {
    const next = new Set(this.expanded());
    if (!next.delete(planId)) next.add(planId);
    this.expanded.set(next);
  }

  protected onRowKeydown(event: KeyboardEvent, planId: string): void {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggle(planId);
  }

  /** Ausführungen des Laufs mit seiner Art; ohne Lauf kein Link. */
  protected executionsLink(fall: ReleasePlanCase): TestRunLink | null {
    return fall.laufId && fall.laufArt ? testRunExecutionsLink(this.projectId(), fall.laufId, fall.laufArt) : null;
  }

  protected readonly passRateTone = passRateTone;
}
